import { LoiForm } from './loi-form';
import { LoiFormFairValues } from './loi-form-fair-values';
import { LegalIssueItem } from './legal-issue-item';
import { TermForm } from './term-form';
import { LoiFormsProcessStep, LoiFormsProcess } from '../loi-comparison/models/loi-forms-process.model';
import createNotification from '../../../utils/createNotification';
import { getApiErrorDetail } from '../../../utils/getApiErrorDetail';

export interface PatchLoiFormPayload {
    termForm?: TermForm | null;
    legalIssues?: LegalIssueItem[];
    legalIssueSectionIds?: string[];
    additionalCovenants?: LegalIssueItem[];
}

export interface LoiFormForReviewContract {
    buyerId: number;
    buyerName: string;
    loiFormId: string;
    documentDetailsId: string | null;
    documentName: string | null;
    deadline: string | null;
    isBuyerSubmitted: boolean;
    isAdminSubmitted: boolean;
    isBuyerConfirmed: boolean;
    isLoiPrepared: boolean;
    isResubmission: boolean;
    loiProcessStep: LoiFormsProcessStep;
    loiFormsProcesses: LoiFormsProcess[];
}

export async function fetchLoiForm(
    axios: { get: (url: string) => Promise<{ data: LoiForm; status: number }> },
    loiFormId: string
): Promise<LoiForm> {
    const response = await axios.get(
        `/api/Marketplace/LoiForms/${loiFormId}`
    );
    if (response.status !== 200) {
        throw new Error('Failed to fetch LOI form');
    }
    return response.data as LoiForm;
}

export async function updateLoiForm(
    axios: {
        put: (url: string, data: LoiForm) => Promise<{ status: number }>;
    },
    loiFormId: string,
    payload: LoiForm
): Promise<void> {
    const response = await axios.put(
        `/api/Marketplace/LoiForms/${loiFormId}`,
        payload
    );
    if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to update LOI form');
    }
}

export async function saveLoiFormFairValues(
    axios: {
        put: (url: string, data: LoiFormFairValues) => Promise<{ data: string; status: number }>;
    },
    engagementId: number,
    loiFormId: string,
    payload: LoiFormFairValues
): Promise<string> {
    const response = await axios.put(
        `/api/Marketplace/${engagementId}/LoiForms/${loiFormId}/FairValues`,
        payload
    );
    if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to save LOI form fair values');
    }
    return response.data;
}

export async function patchLoiForm(
    axios: { patch: (url: string, data: PatchLoiFormPayload) => Promise<{ status: number }> },
    loiFormId: string,
    payload: PatchLoiFormPayload
): Promise<void> {
    const response = await axios.patch(
        `/api/Marketplace/LoiForms/${loiFormId}`,
        payload
    );
    if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to auto-save LOI form');
    }
}

export async function setVendorContractAssumption(
    axios: { patch: (url: string, data: { tableRowId: number; isAssumed: boolean }) => Promise<{ status: number }> },
    loiFormId: string,
    body: { tableRowId: number; isAssumed: boolean }
): Promise<void> {
    const response = await axios.patch(
        `/api/Marketplace/LoiForms/${loiFormId}/VendorContractAssumption`,
        body
    );
    if (response.status !== 200) {
        createNotification('Failed to save vendor contract assumption.', 'error');
        throw new Error('Failed to save vendor contract assumption');
    }
}

export async function fetchLoiFormAdminTemplate(
    axios: { get: (url: string) => Promise<{ data: LoiForm; status: number }> }
): Promise<LoiForm> {
    const response = await axios.get('/api/Marketplace/LoiForms');
    if (response.status !== 200) {
        throw new Error('Failed to fetch LOI form admin template');
    }
    return response.data as LoiForm;
}

export async function submitLoiForm(
    axios: { post: (url: string, data: { eventId: string; loiFormId: string }) => Promise<{ status: number }> },
    engagementId: number,
    eventId: string,
    loiFormId: string
): Promise<void> {
    const response = await axios.post(
        `/api/Marketplace/${engagementId}/MarketplaceEventActions/SubmitLoi`,
        { eventId, loiFormId }
    );
    if (response.status !== 200 && response.status !== 204) {
        const message = getApiErrorDetail(response);
        throw new Error(message ?? 'Failed to submit LOI form');
    }
}

export async function fetchLoiFormsForReview(
    axios: { get: (url: string) => Promise<{ data: LoiFormForReviewContract[]; status: number }> },
    engagementId: number
): Promise<LoiFormForReviewContract[]> {
    const response = await axios.get(
        `/api/Marketplace/${engagementId}/LoiForms/ForVetValueReview`
    );
    if (response.status !== 200) {
        throw new Error('Failed to fetch LOI forms for review');
    }
    return response.data as LoiFormForReviewContract[];
}

export async function submitAdminLoiForms(
    axios: { post: (url: string, data: { eventId: string; loiFormIds: string[] }) => Promise<{ status: number }> },
    engagementId: number,
    eventId: string,
    loiFormIds: string[]
): Promise<void> {
    const response = await axios.post(
        `/api/Marketplace/${engagementId}/MarketplaceEventActions/SubmitAdminLoiForms`,
        { eventId, loiFormIds }
    );
    if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to submit admin LOI forms');
    }
}

export async function confirmLoiReview(
    axios: { post: (url: string, data: { eventId: string }) => Promise<{ status: number }> },
    engagementId: number,
    eventId: string
): Promise<void> {
    const response = await axios.post(
        `/api/Marketplace/${engagementId}/MarketplaceEventActions/ConfirmLoiReview`,
        { eventId }
    );
    if (response.status !== 200 && response.status !== 204) {
        throw new Error('Failed to confirm LOI review');
    }
}

export async function fetchLoiFormsByIds(
    axios: { post: (url: string, data: string[]) => Promise<{ data: LoiForm[]; status: number }> },
    loiFormIds: string[]
): Promise<LoiForm[]> {
    const response = await axios.post(
        '/api/Marketplace/LoiForms/batch',
        loiFormIds
    );
    if (response.status !== 200) {
        throw new Error('Failed to fetch LOI forms');
    }
    return response.data as LoiForm[];
}
